import type { TelegramState } from '@alphadesk/web/contract.ts';
import { Send } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { type Outcome, useToastedWrite } from '../lib/act.ts';
import { useNow } from '../lib/clock.ts';
import { istStamp } from '../lib/format.ts';
import {
  chatName,
  mintTelegramLink,
  sendTelegramTest,
  unlinkTelegram,
  useTelegramState,
} from '../lib/telegram.ts';
import { PageSection, Row, Rows } from './pages/Sections.tsx';
import { Button } from './primitives/button.tsx';
import { linkClass } from './primitives/controls.tsx';
import { Hint, Well } from './primitives/shell.tsx';
import { useToast } from './Toast.tsx';

interface LinkCode {
  url: string;
  expiresAt: number;
}

const secondsLeft = (until: number, now: number) => Math.max(0, Math.ceil((until - now) / 1000));

const clock = (seconds: number) =>
  `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;

const linkedLine = (chat: NonNullable<TelegramState['link']>): string =>
  `Linked ${istStamp(chat.linkedAt)}${
    chat.lastSentAt ? ` · last message ${istStamp(chat.lastSentAt)}` : ' · nothing sent yet'
  }`;

/** At the foot of the inbox, and on the account page at `#telegram`. */
export function TelegramSection({ framed }: { framed: TelegramState | null }) {
  const { state, refresh } = useTelegramState(framed);
  const [minted, setMinted] = useState<LinkCode | null>(null);
  const { run, busy } = useToastedWrite();
  const toast = useToast();

  // A new link is the code spent: the chat pressed Start.
  const linkedAt = state?.link?.linkedAt ?? null;
  useEffect(() => {
    if (linkedAt !== null) setMinted(null);
  }, [linkedAt]);

  const act = useCallback(
    async (write: () => Promise<Outcome>, said: string) => {
      if (!(await run(write)).ok) return;
      // The socket restates the link too, but read now in case it is offline.
      refresh();
      toast.done(said);
    },
    [run, toast, refresh],
  );

  const link = useCallback(async () => {
    const offer = await run(mintTelegramLink);
    if (!offer.ok) return;
    setMinted({ url: offer.value.url, expiresAt: new Date(offer.value.expiresAt).getTime() });
    // Also drawn below, so a blocked popup is not a dead end.
    window.open(offer.value.url, '_blank', 'noopener,noreferrer');
  }, [run]);

  if (state === null || !state.available) return null;
  const chat = state.link;

  return (
    <PageSection
      id="telegram"
      title="Telegram"
      blurb="Every note, on your phone as well. Nothing can be traded from the chat."
      status={chat ? { tone: 'good', label: 'Linked' } : { tone: 'off', label: 'Not linked' }}
    >
      <Rows>
        <Row
          title={chat ? <span className="ph-no-capture">{chatName(chat)}</span> : 'No chat linked'}
          hint={chat ? linkedLine(chat) : 'Link a chat and press Start there.'}
          control={
            chat ? (
              <>
                <Button
                  busy={busy}
                  onClick={() => void act(sendTelegramTest, 'Sent — check Telegram.')}
                >
                  Send a test
                </Button>
                <Button busy={busy} onClick={() => void link()}>
                  Link another
                </Button>
                <Button
                  tone="undo"
                  busy={busy}
                  onClick={() => void act(unlinkTelegram, 'Telegram unlinked.')}
                >
                  Unlink
                </Button>
              </>
            ) : (
              <Button tone="primary" busy={busy} onClick={() => void link()}>
                <Send aria-hidden className="size-3.5" />
                Link Telegram
              </Button>
            )
          }
        >
          {minted && (
            <Waiting
              minted={minted}
              replacing={chat ? chatName(chat) : null}
              onAgain={() => void link()}
            />
          )}
        </Row>
      </Rows>
    </PageSection>
  );
}

function Waiting({
  minted,
  replacing,
  onAgain,
}: {
  minted: LinkCode;
  replacing: string | null;
  onAgain: () => void;
}) {
  const left = secondsLeft(minted.expiresAt, useNow(1000).getTime());

  return (
    <Well>
      <span aria-live="polite" className="text-xs font-medium text-ink">
        {left === 0 ? 'That code has expired.' : 'Waiting for you in Telegram'}
      </span>
      <Hint as="span" faint>
        {left === 0
          ? 'Codes last ten minutes. Ask for a fresh one and press Start again.'
          : `Open the chat and press Start, and that chat is linked.${replacing === null ? '' : ` Your notifications move off ${replacing}.`} The link is yours alone: anyone who presses Start with it gets your notifications.`}
      </Hint>
      {left > 0 && (
        <a
          href={minted.url}
          target="_blank"
          rel="noopener noreferrer"
          className={linkClass('quiet', 'truncate text-xs text-ink-muted')}
        >
          Open Telegram
        </a>
      )}
      <div className="flex items-center gap-2">
        <span className="tnum text-xs text-ink-faint">
          {left === 0 ? 'Expired' : `Expires in ${clock(left)}`}
        </span>
        <Button onClick={onAgain}>{left === 0 ? 'Get a fresh code' : 'Start again'}</Button>
      </div>
    </Well>
  );
}
